import { CommonRegistry, GlobalTransformData, TransformData } from '@common/components/components';
import { World } from '@common/World';

import {
  CommonFilteredComponentSystem,
  FilteredComponentSystemUpdateEntityArgs,
} from './SystemBase';

/**
 * Computes GlobalTransform for all entities with Transform.
 *
 * For root entities (no ChildOf), GlobalTransform = Transform.
 * For children, GlobalTransform = parent's GlobalTransform composed with child's Transform.
 *
 * Must run after movement systems, before collision and rendering.
 */
export class TransformPropagationSystem extends CommonFilteredComponentSystem {
  public componentClasses: (keyof CommonRegistry)[] = ['Transform'];

  run(deltaTime: number): void {
    if (!this.shouldRun()) {
      return;
    }

    const world = this.getWorld();

    // Find all root entities (have Transform but no ChildOf)
    const rootEntities = world.query(['Transform'], ['ChildOf']);

    // Process each root, then recursively process its descendants (DFS)
    for (const rootId of rootEntities) {
      this.propagateTransform(world, rootId, deltaTime);
    }
  }

  /**
   * Recursively propagate transforms through hierarchy.
   */
  private propagateTransform(
    world: World<CommonRegistry>,
    entityId: number,
    deltaTime: number,
    parentGlobal?: GlobalTransformData,
  ): void {
    const transform = world.getComponent(entityId, 'Transform') as TransformData | undefined;
    if (!transform) return;

    // Get or create GlobalTransform
    let globalTransform = world.getComponent(entityId, 'GlobalTransform') as
      | ({ readonly typeName: 'GlobalTransform' } & GlobalTransformData)
      | undefined;

    if (!globalTransform) {
      globalTransform = world.createComponent('GlobalTransform', {});
      world.addComponent(entityId, globalTransform);
    }

    if (parentGlobal) {
      // Compose with parent transform
      this.composeTransforms(parentGlobal, transform, globalTransform);
    } else {
      // Root entity: GlobalTransform = Transform
      globalTransform.x = transform.x;
      globalTransform.y = transform.y;
      globalTransform.rotation = transform.rotation;
      globalTransform.scaleX = transform.scaleX;
      globalTransform.scaleY = transform.scaleY;
      globalTransform.sceneId = transform.sceneId;
    }

    // Process children
    const children = world.getChildren(entityId);
    for (const childId of children) {
      this.propagateTransform(world, childId, deltaTime, globalTransform);
    }
  }

  /**
   * Compose parent and child transforms into result.
   *
   * Position: rotate child offset by parent rotation, scale by parent scale, add to parent pos
   * Rotation: additive
   * Scale: multiplicative
   */
  private composeTransforms(
    parent: GlobalTransformData,
    child: TransformData,
    result: GlobalTransformData,
  ): void {
    // Scene membership inherited from parent
    result.sceneId = parent.sceneId;

    const cos = Math.cos(parent.rotation);
    const sin = Math.sin(parent.rotation);

    // Position: apply parent's scale to child's local offset, then rotate, then translate
    const scaledChildX = child.x * parent.scaleX;
    const scaledChildY = child.y * parent.scaleY;
    result.x = parent.x + (scaledChildX * cos - scaledChildY * sin);
    result.y = parent.y + (scaledChildX * sin + scaledChildY * cos);

    // Rotation: additive
    result.rotation = parent.rotation + child.rotation;

    // Scale: multiplicative
    result.scaleX = parent.scaleX * child.scaleX;
    result.scaleY = parent.scaleY * child.scaleY;
  }

  // Override updateEntity since we use custom run() logic
  updateEntity({
    entityId,
    deltaTime,
  }: FilteredComponentSystemUpdateEntityArgs<CommonRegistry>): void {
    void entityId;
    void deltaTime;
  }
}
